import { useParentPosition } from "@/hooks/useParentPosition";
import { useEffect, useRef, useState } from "react";
import { IconChevronDown } from "@tabler/icons-react";

type EmailStatusInputProps = {
  options: string[];
  initialValue: string;
  setParent: (value: string) => void;
  onFocusAction?: () => void;
  height?: string;
  border?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  disabled?: boolean;
};

export const EmailStatusInput: React.FC<EmailStatusInputProps> = ({
  options,
  initialValue,
  setParent,
  onFocusAction,
  height = "h-9",
  border = "",
  surface = "surface-primary",
  fontSize = "body-2-regular",
  padding = "px-2 py-1",
  rounded = "rounded-lg",
  disabled,
}) => {
  const isMouseDownOnDropdown = useRef(false);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(initialValue);
  const [inputWidth, setInputWidth] = useState<number>(0);
  const [positionReady, setPositionReady] = useState(false);

  const { dropdownPosition, setDropdownPosition } = useParentPosition();
  const divRef = useRef<HTMLDivElement>(null);

  // Follow an externally replaced value — same reason as GuestStatusInput:
  // this control only ever renders in the sent-mail FILTER row, and a cleared
  // filter has to clear the box with it instead of leaving the old status
  // sitting there.
  useEffect(() => {
    setSelectedValue(initialValue);
  }, [initialValue]);

  // ---------------------------------------------------------
  // 1. Live Tracking Effect (Scroll & Resize)
  // ---------------------------------------------------------
  useEffect(() => {
    if (showOptions && divRef.current) {
      const updatePosition = () => {
        if (divRef.current) setDropdownPosition(divRef);
      };

      // Capture phase is required for nested scrolling
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showOptions, setDropdownPosition]);

  // ---------------------------------------------------------
  // 2. Immediate Failsafe Effect
  // ---------------------------------------------------------
  useEffect(() => {
    if (showOptions && divRef.current) {
      // Force calculation immediately when state becomes true
      setDropdownPosition(divRef);
      // Sync width
      setInputWidth(divRef.current.offsetWidth);
    }
  }, [showOptions, setDropdownPosition]);

  // ---------------------------------------------------------
  // 3. Wait for position to be calculated before marking ready
  // ---------------------------------------------------------
  useEffect(() => {
    if (showOptions && dropdownPosition.top !== 0) {
      setPositionReady(true);
    }
  }, [showOptions, dropdownPosition]);

  // ---------------------------------------------------------
  // 4. Centralized Open Logic
  // ---------------------------------------------------------
  const handleToggle = () => {
    // Prevent toggling when disabled is true
    if (disabled) return;

    if (showOptions) {
      // Closing
      setShowOptions(false);
      setPositionReady(false);
    } else {
      // Opening
      if (onFocusAction) onFocusAction();

      // Order of Operations: Calculate FIRST, then Show
      if (divRef.current) {
        setDropdownPosition(divRef);
        setInputWidth(divRef.current.offsetWidth);
      }
      setPositionReady(false);
      setShowOptions(true);
    }
  };

  const handleSelection = (value: string) => {
    // Prevent selection when disabled is true
    if (disabled) return;

    setSelectedValue(value);
    setParent(value);
    setShowOptions(false);
    setPositionReady(false);
  };

  return (
    <>
      <div className="relative w-full">
        <div
          ref={divRef}
          onClick={handleToggle}
          onMouseEnter={() => !disabled && setDropdownPosition(divRef)}
          className={`${padding} ${surface} ${rounded} ${height} ${border} w-full flex items-center ${fontSize} pr-8 ${
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          } outline-quaternary`}
        >
          <div className="flex flex-row w-full">
            <p>{selectedValue}</p>
          </div>
        </div>
        <IconChevronDown
          size={16}
          className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${
            showOptions ? "rotate-180" : ""
          } ${disabled ? "opacity-50" : ""}`}
        />
      </div>

      {showOptions && !disabled && positionReady && (
        <div
          className="fixed z-20 inset-0 h-screen w-screen"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ul
            className="fixed p-2 z-20 space-y-2 mt-1 surface-primary border rounded-lg max-h-60 overflow-y-auto outline-quaternary"
            style={{
              ...(dropdownPosition.showAbove
                ? {
                    bottom: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }
                : {
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }),
              minWidth: `${inputWidth}px`,
              width: "max-content",
              maxWidth: `min(20rem, calc(100vw - ${dropdownPosition.left}px - 16px))`,
            }}
            onMouseDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
          >
            {options.map((option, index) => (
              <li
                key={`option-${index}`}
                className={`py-1 cursor-pointer w-full  body-3-regular px-2 flex rounded-lg items-center justify-between`}
                onClick={() => handleSelection(option)}
              >
                <span>{option}</span>
              </li>
            ))}
          </ul>
          <div
            className="fixed inset-0 h-screen w-screen"
            onClick={() => {
              setShowOptions(false);
              setPositionReady(false);
            }}
          />
        </div>
      )}
    </>
  );
};
